// Estilos compartilhados das telas anônimas (Login e Cadastro) no padrão
// Aeros Dark: split hero/formulário, blobs de gradiente, grade mascarada e
// campos com ícone. Antes cada tela tinha o próprio makeStyles com o mesmo
// conteúdo divergindo.
//
// Estas telas são vistas por quem ainda não escolheu tema, então o hero é
// sempre escuro (como o mockup); o painel do formulário respeita o tema.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "./aerosTokens";

const useAerosAuthStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";

  return {
    // ---- casca ----
    root: {
      minHeight: "100vh",
      display: "grid",
      // `heroWide` (login) e `heroNarrow` (cadastro) ajustam a proporção.
      gridTemplateColumns: "minmax(0,1.15fr) minmax(0,1fr)",
      backgroundColor: isLight ? theme.palette.aeros.background : "#07080D",
      color: theme.palette.text.primary,
      [theme.breakpoints.down("sm")]: {
        // `minmax(0,1fr)`, nunca `1fr`: a trilha auto adota a largura fixa do
        // cartão (400px) como mínimo e o `maxWidth: 100%` dele nunca vale.
        gridTemplateColumns: "minmax(0,1fr)",
      },
    },
    heroNarrow: {
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.3fr)",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },

    // ---- painel esquerdo (hero) ----
    hero: {
      position: "relative",
      overflow: "hidden",
      backgroundColor: "#0B0D14",
      color: "#F1F2F6",
      display: "flex",
      flexDirection: "column",
      padding: "48px 56px",
      backgroundSize: "cover",
      backgroundPosition: "center",
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    // Blobs radiais e a grade só entram quando não há imagem de whitelabel.
    heroBlobPurple: {
      position: "absolute",
      width: 720,
      height: 720,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(160,68,255,0.35), transparent 65%)",
      left: -200,
      top: -160,
      filter: "blur(20px)",
      pointerEvents: "none",
    },
    heroBlobCyan: {
      position: "absolute",
      width: 620,
      height: 620,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(34,211,238,0.28), transparent 65%)",
      right: -220,
      bottom: -200,
      filter: "blur(20px)",
      pointerEvents: "none",
    },
    heroGrid: {
      position: "absolute",
      inset: 0,
      backgroundImage:
        "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
      backgroundSize: "48px 48px",
      maskImage:
        "radial-gradient(circle at 40% 45%, #000 20%, transparent 75%)",
      WebkitMaskImage:
        "radial-gradient(circle at 40% 45%, #000 20%, transparent 75%)",
      pointerEvents: "none",
    },
    // Véu sobre a imagem de whitelabel: o headline, os números e o rodapé
    // continuam por cima, então o meio precisa escurecer mais que o antigo 45%.
    heroVeil: {
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(180deg, rgba(7,8,13,0.78) 0%, rgba(7,8,13,0.62) 45%, rgba(7,8,13,0.85) 100%)",
      pointerEvents: "none",
    },
    heroContent: { position: "relative" },
    heroBrand: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    brandMark: {
      width: 36,
      height: 36,
      borderRadius: 10,
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#07080D",
      fontWeight: 800,
      fontSize: 18,
      flex: "none",
      overflow: "hidden",
      "& img": { width: "100%", height: "100%", objectFit: "contain" },
    },
    // Logomarca da whitelabel (ou a padrão) no topo do painel esquerdo.
    brandLogo: {
      display: "block",
      // Logos de cliente costumam vir com muita margem transparente; 72px de
      // altura deixa a marca legível sem competir com o headline.
      height: 72,
      maxWidth: 260,
      objectFit: "contain",
      objectPosition: "left center",
    },
    brandName: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 19,
      fontWeight: 700,
      color: "#F1F2F6",
    },
    heroBody: {
      position: "relative",
      margin: "auto 0",
      maxWidth: 520,
      display: "flex",
      flexDirection: "column",
      gap: 22,
    },
    heroEyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: AEROS.cyan,
    },
    heroTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 46,
      fontWeight: 800,
      letterSpacing: "-0.03em",
      lineHeight: 1.05,
      color: "#F1F2F6",
      [theme.breakpoints.down("md")]: { fontSize: 36 },
    },
    heroTitleSmall: {
      fontSize: 38,
      lineHeight: 1.08,
      [theme.breakpoints.down("md")]: { fontSize: 30 },
    },
    heroText: {
      fontSize: 15,
      color: "#9CA3B8",
      lineHeight: 1.6,
    },
    heroStats: { display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" },
    heroStat: {
      background: "rgba(18,21,31,0.7)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 12,
      padding: "12px 16px",
      backdropFilter: "blur(8px)",
      minWidth: 130,
    },
    heroStatValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.1,
      color: "#F1F2F6",
    },
    heroStatLabel: { fontSize: 11.5, color: "#9CA3B8", marginTop: 3 },
    heroFooter: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 14,
      fontSize: 11.5,
      color: "#5B6376",
      flexWrap: "wrap",
    },

    // ---- passos do cadastro (painel esquerdo) ----
    steps: { display: "flex", flexDirection: "column", gap: 14 },
    step: { display: "flex", gap: 12, alignItems: "flex-start" },
    stepDot: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      fontSize: 12,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    stepDone: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      border: "1px solid transparent",
      color: "#07080D",
    },
    stepCurrent: {
      background: "rgba(160,68,255,0.18)",
      border: "1px solid rgba(160,68,255,0.6)",
      color: "#C08BFF",
    },
    stepUpcoming: {
      background: "transparent",
      border: "1px solid rgba(255,255,255,0.15)",
      color: "#5B6376",
    },
    stepTitle: { fontSize: 14, fontWeight: 600, color: "#F1F2F6" },
    stepTitleUpcoming: { color: "#9CA3B8" },
    stepDesc: { fontSize: 12.5, color: "#9CA3B8", lineHeight: 1.5 },

    // ---- painel direito (formulário) ----
    formPane: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 48,
      position: "relative",
      backgroundColor: isLight ? theme.palette.aeros.background : "#07080D",
      [theme.breakpoints.down("xs")]: { padding: 24 },
    },
    paneTopRight: {
      position: "absolute",
      top: 24,
      right: 24,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    squareButton: {
      width: 36,
      height: 36,
      borderRadius: 10,
      border: `1px solid ${borderInput}`,
      backgroundColor: theme.palette.aeros.surface,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": {
        color: theme.palette.text.primary,
        borderColor: alpha(AEROS.cyan, 0.4),
        backgroundColor: theme.palette.aeros.surface,
      },
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    formCard: {
      width: 400,
      maxWidth: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 22,
    },
    formCardWide: { width: 620 },
    formTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 26,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
    },
    formTitleSmall: { fontSize: 24 },
    formSubtitle: {
      fontSize: 13.5,
      color: theme.palette.text.secondary,
      marginTop: 4,
      lineHeight: 1.5,
    },
    fields: { display: "flex", flexDirection: "column", gap: 14 },

    // ---- campos ----
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "flex",
      alignItems: "center",
    },
    labelFocus: { color: stateText(theme, AEROS.cyan, "info") },
    labelRequired: { color: AEROS.error, marginLeft: 3 },
    labelAside: {
      marginLeft: "auto",
      fontSize: 12,
      fontWeight: 600,
      color: stateText(theme, "#C08BFF", "purple"),
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
    },
    input: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        backgroundColor: theme.palette.aeros.surface,
        borderRadius: 10,
        fontSize: 14,
        "& fieldset": { borderColor: borderInput },
        "&:hover fieldset": { borderColor: theme.palette.aeros.borderStrong },
        "&.Mui-focused fieldset": {
          borderColor: AEROS.cyan,
          borderWidth: 1,
        },
        "&.Mui-focused": {
          boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}`,
        },
        "&.Mui-error fieldset": { borderColor: AEROS.error },
        "&.Mui-error.Mui-focused": {
          boxShadow: `0 0 0 3px ${alpha(AEROS.error, 0.15)}`,
        },
      },
      "& .MuiOutlinedInput-input": { padding: "11px 14px" },
      "& .MuiOutlinedInput-adornedStart .MuiOutlinedInput-input": {
        paddingLeft: 4,
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.aeros.textTertiary,
      },
      "& .MuiFormHelperText-root": { fontSize: 11.5, marginLeft: 2 },
    },
    inputValid: {
      "& .MuiOutlinedInput-root fieldset": {
        borderColor: alpha(AEROS.success, 0.5),
      },
    },
    fieldHintOk: {
      fontSize: 11,
      color: stateText(theme, AEROS.success, "success"),
      marginTop: 5,
    },

    // ---- botões ----
    gradientButton: {
      borderRadius: 10,
      padding: 12,
      fontSize: 14,
      fontWeight: 700,
      boxShadow: "0 8px 24px rgba(160,68,255,0.3)",
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    ghostLink: {
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 600,
      color: stateText(theme, AEROS.cyan, "info"),
      "&:hover": { textDecoration: "underline" },
    },
    backLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": { color: theme.palette.text.primary },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    footNote: {
      textAlign: "center",
      fontSize: 13,
      color: theme.palette.text.secondary,
    },

    // ---- etapas de verificação (2FA / dispositivo) ----
    stepIcon: {
      width: 48,
      height: 48,
      borderRadius: 14,
      background: "rgba(160,68,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: stateText(theme, "#C08BFF", "purple"),
      "& .MuiSvgIcon-root": { fontSize: 26 },
    },
    otpRow: {
      display: "grid",
      gridTemplateColumns: "repeat(6, 1fr)",
      gap: 8,
    },
    otpBox: {
      // `<input>` tem largura intrínseca (~255px pelo `size` padrão) e item de
      // grid nasce com `min-width:auto` — sem zerar isso as seis caixas não
      // encolhem, estouram o cartão e arrastam a página inteira na horizontal.
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      height: 56,
      borderRadius: 10,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      textAlign: "center",
      fontSize: 22,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      outline: "none",
      padding: 0,
      "&:focus": {
        borderColor: AEROS.cyan,
        boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}`,
      },
    },
    progressTrack: { display: "flex", gap: 6 },
    progressSegment: { height: 4, flex: 1, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.1)" },
    progressSegmentOn: {
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
    },
    mandatoryBadge: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: stateText(theme, AEROS.warning, "warning"),
    },
    qrBox: {
      width: 140,
      height: 140,
      borderRadius: 12,
      backgroundColor: "#F1F2F6",
      padding: 10,
      boxSizing: "border-box",
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    secretBox: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      padding: "8px 10px",
      fontFamily: "ui-monospace, monospace",
      fontSize: 12,
      letterSpacing: "0.08em",
      wordBreak: "break-all",
      color: theme.palette.text.primary,
    },
    copyLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11.5,
      fontWeight: 600,
      color: stateText(theme, "#C08BFF", "purple"),
      marginTop: 8,
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      fontFamily: "inherit",
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    recoveryGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    },
    recoveryCode: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      padding: "8px 10px",
      fontFamily: "ui-monospace, monospace",
      fontSize: 13,
      textAlign: "center",
      color: theme.palette.text.primary,
    },

    // ---- medidor de senha ----
    strengthTrack: { display: "flex", gap: 4, marginTop: 6 },
    strengthSegment: {
      height: 3,
      flex: 1,
      borderRadius: 2,
      backgroundColor: "rgba(255,255,255,0.1)",
    },
    strengthLabel: { fontSize: 11, marginTop: 5, fontWeight: 600 },

    // ---- cards de plano ----
    planGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    planCard: {
      border: `1px solid ${borderInput}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 12,
      padding: "12px 14px",
      position: "relative",
      cursor: "pointer",
      textAlign: "left",
      font: "inherit",
      color: theme.palette.text.primary,
      "&:hover": { borderColor: "rgba(160,68,255,0.4)" },
    },
    planCardOn: {
      borderColor: "rgba(160,68,255,0.6)",
      backgroundColor: "rgba(160,68,255,0.1)",
    },
    planCheck: {
      position: "absolute",
      top: 10,
      right: 10,
      width: 18,
      height: 18,
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#07080D",
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    planName: { fontSize: 13, fontWeight: 700 },
    planPrice: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 18,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      marginTop: 4,
    },
    planPer: { fontSize: 11, color: theme.palette.text.secondary, fontWeight: 500 },
    planMeta: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      marginTop: 6,
      lineHeight: 1.5,
    },

    terms: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      lineHeight: 1.5,
      flex: 1,
      "& a": {
        color: theme.palette.text.secondary,
        textDecoration: "underline",
      },
    },
  };
});

export default useAerosAuthStyles;
